import { fromNullable, None } from 'fp-ts/lib/Option';
import { BehaviorSubject, merge, of } from 'rxjs';
import { TAddUpdateLeadGoal } from '../models/add-update-lead-goal';
import { LeadService } from '../lib/das-api/api/lead.service';
import { LeadGoalResponse } from '../lib/das-api/model/leadGoalResponse';
import { TLeadGoalsResponse } from '../models/goals-info-response';
import { LeadApi } from './lead.api';
import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { LeadCategory } from '../models/lead-category-enum';
import { defaultGoalsProfile } from "../constants/data.constant";
import { GOAL_KEY, GOAL_ID_KEY } from '../constants/storage.constant';
import { getRedirectPage } from '../helpers/pagehelper';
import { ModelPerformanceInput } from '../lib/das-api/model/modelPerformanceInput';
import { AddUpdateGoal } from '../lib/das-api/model/addUpdateGoal';
import { OtherRetirementIncome } from '../lib/das-api/model/otherRetirementIncome';
import { AddUpdateLeadGoal } from '../lib/das-api/model/addUpdateLeadGoal';
import { LeadOtherRetirementIncome } from '../lib/das-api/model/leadOtherRetirementIncome';
import { parseGoalTypeToEnum } from '../helpers/goal.helper';
import { AppApi } from './app.api';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace GoalsApi {
	export const redirectToPage$ = new BehaviorSubject<string>('');
	export const goalsData$ = new BehaviorSubject<TLeadGoalsResponse>(defaultGoalsProfile);
	export const getSavedGoalsData$ = () => goalsData$.asObservable(); 

	export const removeLeadGoalDataFromContext = () => {
		// LocalStorageUtils.removeItem(GOAL_KEY);
		goalsData$.next(defaultGoalsProfile);
	};

	// PUT endpoints here
	export const saveLeadGoalDataToLocal = (lead: TAddUpdateLeadGoal, currentRoute: string) => {
		const response$ =  LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdGoalPost(lead.leadId, lead.leadStrategyId, {
			investmentAmount: lead.investmentAmount || 0,
			targetGrowthAmount: lead.targetGrowthAmount || 0,
			targetGoalYear: lead.targetGoalYear || 0,
			monthlyIncome: lead.monthlyIncome || 0,
			monthlyIncomeToRetirePercent: lead.monthlyIncomeToRetirePercent || 0,
			netRetirementMonthlyIncome: lead.netRetirementMonthlyIncome || 0,
			savingsCurrent: lead.savingsCurrent || 0,
			annualSavings: lead.annualSavings || 0,
			investmentTypeEnum: parseGoalTypeToEnum(lead.goalType || 2),
			birthYear: lead.birthYear || 0,
			lifeExpectancy: 90,
			inflation: 0.02,
			agentId: lead.agentId,
		})
		.doRD(data => { 
			LocalStorageUtils.setItem(GOAL_KEY)(data);
		})
		.doRD(data => LeadApi.updateLastVisitedPage({ 
			leadId: lead.leadId, leadStrategyId: lead.leadStrategyId, agentId: lead.agentId, lastPageVisited: LeadCategory.SetGoal, currentRoute
		}, currentRoute))
		.mapRD(parseGoals);
		response$.subscribe(data => {
			if(data.isSuccess()) {
				LocalStorageUtils.setItem(GOAL_ID_KEY)(data.value.id.getOrElse(''));
				goalsData$.next(data.value);
			}
		});
	};

	export const addLeadGoal = (goal: TAddUpdateLeadGoal, currentRoute: string, lastPageVisited: string) => {
		LeadApi.showSpinner$.next(true);
		const response$ = LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdGoalPost(
			goal.leadId,
			goal.leadStrategyId,
			parseGoalRequest(goal),
		)
			.doRD(data => {
				LocalStorageUtils.setItem(GOAL_KEY)(data);
			})
			.doRD(data =>
				LeadApi.updateLastVisitedPage(
					{
						leadId: goal.leadId,
						leadStrategyId: goal.leadStrategyId,
						agentId: goal.agentId,
						lastPageVisited: lastPageVisited,
						currentRoute,
					},
					`${goal.goalType}`,
				),
			)
			.mapRD(parseGoals);
		response$.subscribe(data => {
			if (data.isSuccess()) {
				LocalStorageUtils.setItem(GOAL_ID_KEY)(data.value.id.getOrElse(''));
				goalsData$.next(data.value);
			}
		});
	};

	export const updateLeadGoal = (goal: TAddUpdateLeadGoal, modelPerformanceInput: ModelPerformanceInput, currentRoute: string, lastPageVisited: string) => {
		LeadApi.showSpinner$.next(true);
		const response$ =  LeadService.apiDasV1LeadLeadIdLeadstrategiesLeadStrategyIdGoalGoalIdPut(goal.leadId, goal.leadStrategyId, goal.id || '', { goal: parseGoalRequest(goal), modelPerformanceInput })
		.doRD(data => {
			LocalStorageUtils.setItem(GOAL_KEY)(data);
		})
		.doRD(data => LeadApi.updateLastVisitedPage({ 
			leadId: goal.leadId, leadStrategyId: goal.leadStrategyId, agentId: goal.agentId, lastPageVisited: lastPageVisited, currentRoute
		}, `${goal.goalType}`))
		.mapRD(parseGoals);

		response$.subscribe(updatedGoal => { 
			//console.log(`updatedGoal: `, updatedGoal);
			if(updatedGoal.isSuccess()) {
				goalsData$.next(updatedGoal.value);
			}
			});
	};

	export const parseGoalRequest = (goal: TAddUpdateLeadGoal): AddUpdateLeadGoal => {
		return {
			agentId: goal.agentId,
			investmentAmount: goal.investmentAmount,
			targetGrowthAmount: goal.targetGrowthAmount,
			targetGoalYear: goal.targetGoalYear,
			monthlyIncome: goal.monthlyIncome,
			monthlyIncomeToRetirePercent: goal.monthlyIncomeToRetirePercent,
			savingsCurrent: goal.savingsCurrent,
			annualSavings: goal.annualSavings,
			investmentTypeEnum: parseGoalTypeToEnum(goal.goalType),
			birthYear: goal.birthYear,
			lifeExpectancy: goal.lifeExpectancy,
			inflation: goal.inflation,
			netRetirementMonthlyIncome: goal.netRetirementMonthlyIncome,
			otherRetirementIncomes: goal.otherRetirementIncomes ? goal.otherRetirementIncomes.map(ot => ({
				otherRetirementIncomeID: ot.otherRetirementIncomeID,
				incomeSourceTypeId: ot.incomeSourceTypeId,
				monthlyIncome: ot.monthlyIncome || 0
			})) : [] as LeadOtherRetirementIncome[],
		};
	};

	export const parseGoals = (lead: LeadGoalResponse): TLeadGoalsResponse => {
	// const parseGoals = (lead: any): any => {
	return {
		investmentAmount: fromNullable(lead.investmentAmount),
		targetGrowthAmount: fromNullable(Number(lead.targetGrowthAmount)),
		targetGoalYear: fromNullable(Number(lead.targetGoalYear)),
		monthlyIncome: fromNullable(Number(lead.monthlyIncome)),
		monthlyIncomeToRetirePercent: fromNullable(Number(lead.monthlyIncomeToRetirePercent)),
		netRetirementMonthlyIncome: fromNullable(Number(lead.netRetirementMonthlyIncome)),
		leadOtherRetirementIncome: fromNullable(lead.leadOtherRetirementIncome),
		savingsCurrent: fromNullable(Number(lead.savingsCurrent)),
		annualSavings: fromNullable(Number(lead.annualSavings)),
		birthYear: fromNullable(Number(lead.birthYear)),
		lifeExpectancy: fromNullable(Number(lead.lifeExpectancy)),
		inflation: fromNullable(Number(lead.inflation)),
		agentId: fromNullable(String(lead.agentId)),
		createdDate: fromNullable(lead.createdDate),
		id: fromNullable(String(lead.id)),
		investmentTypeEnum: fromNullable(lead.investmentTypeEnum)
	};
};
}

